import { FRONTIER_LANDMARKS, FRONTIER_TRAILS } from './frontier.js';
import { LANTERN_PYLONS } from './gameplay.js';
import * as THREE from 'three';
import { createThreeEffects, enhanceSurface } from './environment-effects.js';
import { createCutaway } from './cutaway.js';
import { createWorldAssetLoader } from './world-assets.js';
import { PLAYABLE_BOUND, TERRAIN_SIZE } from './exploration.js';
import { trailWeight, snappedShadowFocus } from './presentation.js';

// Each district now carries its own vertical sky ramp (zenith -> mid -> horizon), a moon tint, a
// star density, and a fog density, so a district swap repaints the whole atmosphere instead of
// swapping one flat background colour. `ground` is the albedo the vertex-coloured floor multiplies.
export const DISTRICTS = {
  brackenwake: { name: 'BRACKENWAKE', title: 'The village that remembers', objective: 'Restore the lantern line', color: 0xd9b96f, fog: 0x3a3350, ground: 0x6b6478, accent: 0xe8bb69, zenith: 0x16132f, mid: 0x5c4d7a, moon: 0xf4ecd8, stars: 0.7, fogDensity: 0.02, spawn: new THREE.Vector3(0, 0, 10) },
  greyfen: { name: 'GREYFEN', title: 'Where the pack keeps its names', objective: 'Follow the silver-marked path', color: 0x91bf7a, fog: 0x2b463c, ground: 0x5c7a5e, accent: 0x9fbf7a, zenith: 0x0d2028, mid: 0x2f6455, moon: 0xe2f2e6, stars: 0.6, fogDensity: 0.027, spawn: new THREE.Vector3(0, 0, 16) },
  'red-chapel': { name: 'RED CHAPEL', title: 'A court without a king', objective: 'Break the blood ritual', color: 0xe26b78, fog: 0x4a2749, ground: 0x7a4568, accent: 0xf2c36e, zenith: 0x230d1e, mid: 0x7a2743, moon: 0xffdcb4, stars: 0.5, fogDensity: 0.021, spawn: new THREE.Vector3(0, 0, -17) },
  ironpass: { name: 'IRONPASS', title: 'The pass of empty lanterns', objective: 'Cross the Covenant road', color: 0x9ba8bf, fog: 0x42455c, ground: 0x7b7d8c, accent: 0xb9c5d1, zenith: 0x141828, mid: 0x4d5573, moon: 0xe8eff8, stars: 0.9, fogDensity: 0.022, spawn: new THREE.Vector3(-16, 0, -3) },
  'duskfall-crypt': { name: 'DUSKFALL CRYPT', title: 'The first wound', objective: 'Descend beneath the old kings', color: 0x8b79ee, fog: 0x2b2350, ground: 0x554b8c, accent: 0xaa95ff, zenith: 0x120c2e, mid: 0x3d2f7e, moon: 0xe8deff, stars: 1, fogDensity: 0.024, spawn: new THREE.Vector3(16, 0, 17) },
  hollow: { name: 'THE HOLLOW', title: 'No census, no masters', objective: 'Return the names', color: 0x6de4e0, fog: 0x1f3a55, ground: 0x4d7f90, accent: 0x6de4e0, zenith: 0x0a1a33, mid: 0x1a6478, moon: 0xdff8f4, stars: 1, fogDensity: 0.023, spawn: new THREE.Vector3(23, 0, 0) },
  moonwell: { name: 'MOONWELL', title: 'The drowned lantern', objective: 'Find the Ember Sigil', color: 0x73d8d5, fog: 0x234270, ground: 0x4c6a95, accent: 0xe8bb69, zenith: 0x0b1433, mid: 0x245a86, moon: 0xe4efe8, stars: 1, fogDensity: 0.023, spawn: new THREE.Vector3(0, 0, 7) },
  chapel: { name: 'RED CHAPEL', title: 'A court without a king', objective: 'Find the Fang Sigil', color: 0xe26b78, fog: 0x4a2749, ground: 0x7a4568, accent: 0xf2c36e, zenith: 0x230d1e, mid: 0x7a2743, moon: 0xffdcb4, stars: 0.5, fogDensity: 0.021, spawn: new THREE.Vector3(0, 0, -17) },
  woods: { name: 'ASH WOODS', title: 'The pack remembers', objective: 'Find the Veil Sigil', color: 0xa1a0e8, fog: 0x2c2b4e, ground: 0x555a7a, accent: 0x8ed9ba, zenith: 0x101036, mid: 0x334a76, moon: 0xe2f2e8, stars: 0.95, fogDensity: 0.025, spawn: new THREE.Vector3(18, 0, 8) },
};
// `Color.lerp` reads `.r/.g/.b` off its argument, so handing it a district hex number silently
// produces NaN — and a NaN light colour blacks out every lit material in the scene while the
// unlit sky, mist and impact cues keep drawing, which is exactly how this reached production.
const mixScratch = new THREE.Color();
export function isLightStateFinite(world) {
  const lights = [world.hemi, world.hemi?.groundColor, world.key, world.bounce, world.moonLight, ...(world.slotLights || [])];
  return lights.every((entry) => {
    if (!entry) return true;
    const channels = entry.color ? [entry.color.r, entry.color.g, entry.color.b, entry.intensity] : [entry.r, entry.g, entry.b];
    return channels.every((value) => Number.isFinite(value));
  });
}

const SIGILS = [
  { id: 'ember', district: 'moonwell', label: 'Ember Sigil', position: new THREE.Vector3(-17, 1.7, -11), color: 0xe8bb69 },
  { id: 'fang', district: 'chapel', label: 'Fang Sigil', position: new THREE.Vector3(16, 1.7, -18), color: 0xef6b72 },
  { id: 'veil', district: 'woods', label: 'Veil Sigil', position: new THREE.Vector3(19, 1.7, 17), color: 0xa1a0e8 },
];
const DISTRICT_LANDMARKS = [
  { id: 'brackenwake-bell', districts: ['brackenwake'], style: 'ring', x: -28, z: 18, color: 0xd9b96f, label: 'Bell-tower ash', discovery: 'A ribbon of bell-tower ash marks the names Brackenwake tried to forget.' },
  { id: 'greyfen-shrine', districts: ['greyfen'], style: 'stones', x: -28, z: -22, color: 0x91bf7a, label: 'Drowned shrine', discovery: 'Silver needles point toward a shrine the fen has refused to swallow.' },
  { id: 'red-chapel-sigil', districts: ['red-chapel', 'chapel'], style: 'spire', x: 28, z: -18, color: 0xe26b78, label: 'Red choir seal', discovery: 'A choir seal records a verdict written before the accused arrived.' },
  { id: 'ironpass-bell', districts: ['ironpass'], style: 'ring', x: 6, z: 28, color: 0x9ba8bf, label: 'Empty iron bell', discovery: 'The empty iron bell still carries a message for anyone who listens closely.' },
  { id: 'duskfall-crown', districts: ['duskfall-crypt'], style: 'stones', x: 29, z: 24, color: 0x8b79ee, label: 'Root crown', discovery: 'A root crown has grown around a promise no living witness remembers making.' },
  { id: 'hollow-ledger', districts: ['hollow'], style: 'spire', x: 30, z: 2, color: 0x6de4e0, label: 'Exile ledger', discovery: 'The exile ledger lists a village that appears on no other map.' },
  { id: 'moonwell-lantern', districts: ['moonwell'], style: 'ring', x: -27, z: -18, color: 0x73d8d5, label: 'Drowned lantern', discovery: 'A drowned lantern burns underwater, preserving the first promise Rowan ever broke.' },
  { id: 'woods-mark', districts: ['woods'], style: 'stones', x: 28, z: 17, color: 0xa1a0e8, label: 'Ashwood mark', discovery: 'The Ashwood mark points homeward, but the trail was cut from the inside.' },
  ...FRONTIER_LANDMARKS,
];

const COVER_SPOTS = [
  { x: -24, z: 14, rotation: 0.16 },
  { x: -23, z: -18, rotation: -0.22 },
  { x: 24, z: -14, rotation: 0.12 },
  { x: 7, z: 23, rotation: Math.PI / 2 },
  { x: 24, z: 21, rotation: 0.24 },
  { x: 25, z: 4, rotation: -0.18 },
  { x: -22, z: -14, rotation: 0.2 },
  { x: 23, z: 13, rotation: -0.26 },
];

function makeRng(seed) {
  let value = seed >>> 0;
  return () => {
    value += 0x6D2B79F5;
    let t = value;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function material(color, roughness = 0.92, metalness = 0.02) {
  return new THREE.MeshStandardMaterial({ color, roughness, metalness, flatShading: true });
}

function glowMaterial(color, intensity = 1.5) {
  return new THREE.MeshBasicMaterial({ color, transparent: true, opacity: 0.88, blending: THREE.AdditiveBlending });
}

function addCylinder(group, radius, height, color, y = 0, radial = 6) {
  const mesh = new THREE.Mesh(new THREE.CylinderGeometry(radius, radius * 1.08, height, radial), material(color));
  mesh.position.y = y;
  group.add(mesh);
  return mesh;
}

// 38 trees × 3 meshes was 114 draw calls and 114 materials. Three instanced layers keep the same
// silhouettes and the same per-tree sway, but cost three draws and three materials.
// Layer order mirrors the original per-tree build order so the seeded RNG stream — and therefore
// the whole world layout — is unchanged.
const TREE_LAYERS = [
  { key: 'trunk', y: 1.25, build: () => new THREE.CylinderGeometry(0.22, 0.2376, 2.5, 6), color: 0x3c2630 },
  { key: 'crown', y: 3.2, build: () => new THREE.ConeGeometry(1.25, 2.7, 7), color: 0x172f3b },
  { key: 'lower', y: 2.5, build: () => new THREE.ConeGeometry(1.55, 2.15, 7), color: 0x1b3c43 },
];

const treeInstanceBase = new THREE.Matrix4();
// The lean is a 0.7 degree rotation, so it belongs in the vertex shader: the CPU build rebuilt
// 38 x 3 instance matrices and re-uploaded three buffers every frame to move nothing anyone could
// see from across the glade. Instance matrices are now baked once (translate x scale) and the yaw
// and the lean are per-instance attributes, so a swaying canopy costs one uniform write.
const canopySwayUniform = { value: 0 };
const canopyMaterial = (color) => {
  const mat = material(color);
  mat.onBeforeCompile = (shader) => {
    shader.uniforms.uSwayTime = canopySwayUniform;
    shader.vertexShader = `attribute float aSpin;\nattribute float aPhase;\nuniform float uSwayTime;\n${shader.vertexShader}`.replace(
      '#include <begin_vertex>',
      `#include <begin_vertex>
      float lwLean = sin(uSwayTime * 0.42 + aPhase) * 0.012;
      float lwYawSin = sin(aSpin);
      float lwYawCos = cos(aSpin);
      float lwYx = transformed.x;
      float lwYz = transformed.z;
      transformed.x = lwYawCos * lwYx + lwYawSin * lwYz;
      transformed.z = lwYawCos * lwYz - lwYawSin * lwYx;
      float lwLeanSin = sin(lwLean);
      float lwLeanCos = cos(lwLean);
      float lwZx = transformed.x;
      transformed.x = lwLeanCos * lwZx - lwLeanSin * transformed.y;
      transformed.y = lwLeanSin * lwZx + lwLeanCos * transformed.y;`,
    );
  };
  mat.customProgramCacheKey = () => 'lonewolf-canopy-sway';
  return mat;
};


function addTrees(root, random, count) {
  const trees = [];
  for (let i = 0; i < count; i += 1) {
    const x = -PLAYABLE_BOUND + random() * PLAYABLE_BOUND * 2;
    const z = -PLAYABLE_BOUND + random() * PLAYABLE_BOUND * 2;
    if (FRONTIER_LANDMARKS.some(site => Math.hypot(x - site.x, z - site.z) < 10)) continue;
    if (Math.abs(x) < 5 && z > -26 && z < 25) continue;
    if (Math.hypot(x + 9, z - 10) < 8) continue;
    trees.push({ x, z, scale: 0.72 + random() * 0.8, swayPhase: x * 0.17 + z * 0.11, spin: TREE_LAYERS.map(() => random() * Math.PI) });
  }
  const phases = new Float32Array(trees.length);
  const layers = TREE_LAYERS.map((layer, layerIndex) => {
    const geometry = layer.build();
    geometry.translate(0, layer.y, 0);
    const spins = new Float32Array(trees.length);
    const mesh = new THREE.InstancedMesh(geometry, canopyMaterial(layer.color), trees.length);
    mesh.frustumCulled = false;
    mesh.name = `tree-${layer.key}`;
    for (let index = 0; index < trees.length; index += 1) {
      const tree = trees[index];
      // `makeScale` resets the matrix rather than multiplying into it: a reused matrix that
      // accumulated a scale per tree would shrink the last tree in the glade to nothing.
      treeInstanceBase.makeScale(tree.scale, tree.scale, tree.scale);
      treeInstanceBase.setPosition(tree.x, 0, tree.z);
      mesh.setMatrixAt(index, treeInstanceBase);
      phases[index] = tree.swayPhase;
      spins[index] = tree.spin[layerIndex];
    }
    geometry.setAttribute('aPhase', new THREE.InstancedBufferAttribute(phases, 1));
    geometry.setAttribute('aSpin', new THREE.InstancedBufferAttribute(spins, 1));
    mesh.instanceMatrix.needsUpdate = true;
    root.add(mesh);
    return mesh;
  });
  return { trees, apply: (elapsed) => { canopySwayUniform.value = elapsed; } };
}

function addPath(root, points, color = 0x566a75, width = 2.1) {
  const shape = new THREE.Shape();
  shape.moveTo(points[0][0], points[0][1]);
  for (let i = 1; i < points.length; i += 1) shape.lineTo(points[i][0], points[i][1]);
  for (let i = points.length - 1; i >= 0; i -= 1) {
    const point = points[i];
    shape.lineTo(point[0] + width * 0.08, point[1] - width * 0.5);
  }
  shape.closePath();
  const geometry = new THREE.ShapeGeometry(shape, 2);
  geometry.rotateX(-Math.PI / 2);
  const path = new THREE.Mesh(geometry, material(color, 0.98));
  path.position.y = 0.045;
  root.add(path);
  return path;
}
// A forward renderer evaluates every light in the scene for every lit fragment, so thirteen small
// pool lights scattered across a 76 m glade cost thirteen point-light iterations on the floor the
// player is standing on. Emitters become plain descriptors and a fixed pool of slot lights follows
// the nearest few: the count never changes, so the shader program is compiled once, and a pool
// light that is not near a lantern contributes nothing anyway.
function makeEmitter(color, group, y, range, intensity) {
  return { x: group.position.x, y, z: group.position.z, color, range, intensity, live: true, group };
}


function addLantern(root, x, z, color = 0xe8bb69) {
  const group = new THREE.Group();
  group.position.set(x, 0, z);
  const pole = addCylinder(group, 0.045, 2.8, 0x1c2232, 1.4, 6);
  pole.castShadow = false;
  const cage = new THREE.Mesh(new THREE.OctahedronGeometry(0.22, 0), glowMaterial(color));
  cage.position.y = 2.85;
  group.add(cage);
  const emitter = makeEmitter(color, group, 2.8, 8, 1.4);
  group.userData.cage = cage;
  group.userData.emitter = emitter;
  root.add(group);
  return group;
}

function addSigil(root, spec) {
  const group = new THREE.Group();
  group.position.copy(spec.position);
  const crystal = new THREE.Mesh(new THREE.OctahedronGeometry(0.75, 0), new THREE.MeshStandardMaterial({
    color: spec.color,
    emissive: spec.color,
    emissiveIntensity: 1.3,
    roughness: 0.22,
    metalness: 0.18,
    flatShading: true,
  }));
  crystal.scale.set(0.8, 1.7, 0.8);
  group.add(crystal);
  const ring = new THREE.Mesh(new THREE.TorusGeometry(1.05, 0.035, 6, 28), glowMaterial(spec.color, 1.8));
  ring.rotation.x = Math.PI / 2;
  ring.position.y = -0.3;
  group.add(ring);
  const emitter = makeEmitter(spec.color, group, 0.4, 8, 1.5);
  group.userData = { ...spec, type: 'sigil', crystal, ring, emitter, collected: false };
  root.add(group);
  return group;
}

function addGate(root) {
  const gate = new THREE.Group();
  gate.position.set(0, 0, 0);
  const arch = new THREE.Mesh(new THREE.TorusGeometry(2.3, 0.12, 6, 24, Math.PI), material(0x68798b, 0.7, 0.2));
  arch.rotation.z = Math.PI;
  arch.position.y = 2.3;
  gate.add(arch);
  const base = new THREE.Mesh(new THREE.CylinderGeometry(2.2, 2.2, 0.12, 24), glowMaterial(0x6de4e0, 0.4));
  base.position.y = 0.1;
  gate.add(base);
  const marker = new THREE.Mesh(new THREE.OctahedronGeometry(0.35, 0), glowMaterial(0x6de4e0, 1.4));
  marker.position.y = 2.2;
  gate.add(marker);
  gate.userData = { type: 'gate', marker, base, label: 'Cross the veil' };
  root.add(gate);
  return gate;
}

function addDistrictLayer(root, random) {
  const landmarks = [];
  const covers = [];
  const landmarkBaseGeometry = new THREE.CylinderGeometry(1.05, 1.2, 0.32, 8);
  const landmarkPostGeometry = new THREE.CylinderGeometry(0.18, 0.28, 2.4, 6);
  const landmarkCapGeometry = new THREE.BoxGeometry(1.5, 0.22, 1.5);
  const landmarkArchGeometry = new THREE.TorusGeometry(1, 0.15, 5, 16, Math.PI);
  const beaconGeometry = new THREE.OctahedronGeometry(0.22, 0);
  const baseMaterial = material(0x202a35, 0.98);
  enhanceSurface(baseMaterial, { weathering: 0.16 });

  for (const spec of DISTRICT_LANDMARKS) {
    const group = new THREE.Group();
    group.position.set(spec.x, 0, spec.z);
    group.rotation.y = (random() - 0.5) * 0.36;
    const scale = 0.9 + random() * 0.22;
    group.scale.setScalar(scale);
    const surface = material(spec.color, 0.86, 0.08);
    const base = new THREE.Mesh(landmarkBaseGeometry, baseMaterial);
    base.position.y = 0.16;
    group.add(base);
    if (spec.style === 'ring') {
      const arch = new THREE.Mesh(landmarkArchGeometry, surface);
      arch.rotation.z = Math.PI;
      arch.position.y = 1.45;
      group.add(arch);
    } else {
      const post = new THREE.Mesh(landmarkPostGeometry, surface);
      post.position.y = 1.5;
      group.add(post);
      if (spec.style === 'stones') {
        const side = new THREE.Mesh(landmarkPostGeometry, surface);
        side.position.set(-0.7, 0.85, 0.15);
        side.scale.set(0.72, 0.72, 0.72);
        side.rotation.z = 0.18;
        group.add(side);
      }
      const cap = new THREE.Mesh(landmarkCapGeometry, surface);
      cap.position.y = 2.72;
      group.add(cap);
    }
    const beaconMaterial = glowMaterial(spec.color, 1.6);
    const beacon = new THREE.Mesh(beaconGeometry, beaconMaterial);
    beacon.position.y = 2.92;
    group.add(beacon);
    group.userData = { type: 'landmark', frontier: Boolean(spec.frontier), id: spec.id, discoveryId: `${spec.id}-discovery`, label: spec.label, discovery: spec.discovery, districts: spec.districts, beacon, active: false, discovered: false, phase: random() * Math.PI * 2 };
    root.add(group);
    landmarks.push(group);
  }

  const coverBodyGeometry = new THREE.BoxGeometry(2.2, 1.05, 0.7);
  const coverRailGeometry = new THREE.BoxGeometry(2.4, 0.12, 0.82);
  const coverCueGeometry = new THREE.OctahedronGeometry(0.11, 0);
  const coverBodyMaterial = material(0x303944, 0.98);
  const coverRailMaterial = material(0x59616a, 0.9, 0.08);
  for (const spec of COVER_SPOTS) {
    const group = new THREE.Group();
    group.position.set(spec.x, 0, spec.z);
    group.rotation.y = spec.rotation + (random() - 0.5) * 0.18;
    const scale = 0.92 + random() * 0.2;
    group.scale.setScalar(scale);
    const body = new THREE.Mesh(coverBodyGeometry, coverBodyMaterial);
    body.position.y = 0.56;
    group.add(body);
    const rail = new THREE.Mesh(coverRailGeometry, coverRailMaterial);
    rail.position.y = 1.13;
    group.add(rail);
    const cue = new THREE.Mesh(coverCueGeometry, glowMaterial(0xe8bb69, 1.2));
    cue.position.set(spec.x < 0 ? -0.85 : 0.85, 1.42, 0);
    group.add(cue);
    group.userData = { type: 'cover', cue, phase: random() * Math.PI * 2 };
    root.add(group);
    covers.push(group);
  }

  return { landmarks, covers };
}

// The moon is a bearing, not a mesh: the dome shader draws the disc, so the sky costs one draw and
// the moon never parallaxes as the camera crosses the map. Every other module (ground vertex
// colour, the water streak, the key light) reads the same bearing.
export const MOON_DIRECTION = new THREE.Vector3(-24, 28, -30).normalize();
const MOON_BEARING_X = -24;
const MOON_BEARING_Z = -30;
const MOON_REACH = 78;

const SKY_VERTEX = /* glsl */`varying vec3 vWorldPosition;
void main() {
  vec4 worldPosition = modelMatrix * vec4(position, 1.0);
  vWorldPosition = worldPosition.xyz;
  gl_Position = projectionMatrix * viewMatrix * worldPosition;
}`;

// A three-stop vertical ramp, a skyline glow band biased toward the moon, two crossed aurora
// ribbons, a cell-hashed star field, and a shaded moon with limb darkening and a two-stage halo.
const SKY_FRAGMENT = /* glsl */`uniform vec3 uZenith;
uniform vec3 uMid;
uniform vec3 uHorizon;
uniform vec3 uGlow;
uniform vec3 uMoon;
uniform vec3 uMoonDir;
uniform float uMoonSize;
uniform float uTime;
uniform float uStars;
varying vec3 vWorldPosition;

float hash21(vec2 p) {
  p = fract(p * vec2(123.34, 456.21));
  p += dot(p, p + 45.32);
  return fract(p.x * p.y);
}

void main() {
  vec3 dir = normalize(vWorldPosition - cameraPosition);
  float h = dir.y;

  vec3 color = mix(uHorizon, uMid, smoothstep(-0.04, 0.34, h));
  color = mix(color, uZenith, smoothstep(0.26, 0.95, h));

  vec3 flat3 = normalize(vec3(dir.x, 0.0, dir.z) + 1e-5);
  vec3 flatMoon = normalize(vec3(uMoonDir.x, 0.0, uMoonDir.z));
  float toMoon = 0.5 + 0.5 * max(dot(flat3, flatMoon), 0.0);
  float band = pow(max(0.0, 1.0 - abs(h) * 1.5), 3.0);
  color += uGlow * band * toMoon * 0.5;

  float wave = sin(dir.x * 2.1 + uTime * 0.045) * 0.5 + sin(dir.z * 1.6 - uTime * 0.031 + 1.7) * 0.5;
  float ribbon = smoothstep(0.34, 1.0, wave) * smoothstep(0.03, 0.4, h) * (1.0 - smoothstep(0.5, 0.95, h));
  color += uGlow * ribbon * 0.22;

  vec2 sphere = vec2(atan(dir.z, dir.x) * 0.3183099, asin(clamp(dir.y, -1.0, 1.0)) * 0.6366198);
  vec2 grid = sphere * 190.0;
  vec2 cell = floor(grid);
  float present = step(0.86, hash21(cell + 5.1));
  vec2 jitter = vec2(hash21(cell + 11.3), hash21(cell + 27.9)) - 0.5;
  float star = smoothstep(0.19, 0.0, length(fract(grid) - 0.5 - jitter * 0.72));
  float twinkle = 0.72 + 0.28 * sin(uTime * 1.7 + hash21(cell + 3.7) * 61.0);
  color += vec3(0.78, 0.86, 1.0) * star * present * (0.3 + 0.7 * hash21(cell + 9.4)) * twinkle * smoothstep(-0.01, 0.16, h) * uStars;

  float md = clamp(dot(dir, uMoonDir), -1.0, 1.0);
  float disc = smoothstep(uMoonSize, uMoonSize * 0.82, acos(md));
  if (disc > 0.0) {
    vec3 tangent = normalize(cross(uMoonDir, vec3(0.0, 1.0, 0.0)));
    vec3 bitangent = cross(uMoonDir, tangent);
    vec2 local = vec2(dot(dir, tangent), dot(dir, bitangent)) / uMoonSize;
    float limb = sqrt(max(0.0, 1.0 - clamp(dot(local, local), 0.0, 1.0)));
    float maria = 0.8 + 0.2 * sin(local.x * 3.3 + 1.1) * sin(local.y * 2.6 - 0.6);
    color = mix(color, uMoon * maria * (0.52 + 0.48 * limb), disc);
  }
  color += uMoon * (pow(max(md, 0.0), 260.0) * 0.5 + pow(max(md, 0.0), 14.0) * 0.09);

  gl_FragColor = vec4(color, 1.0);
}`;

function createSkyDome() {
  const geometry = new THREE.SphereGeometry(90, 32, 16);
  const material = new THREE.ShaderMaterial({
    uniforms: {
      uZenith: { value: new THREE.Color(0x050a1c) },
      uMid: { value: new THREE.Color(0x14304a) },
      uHorizon: { value: new THREE.Color(0x111b36) },
      uGlow: { value: new THREE.Color(0x2b6670) },
      uMoon: { value: new THREE.Color(0xe4efe8) },
      uMoonDir: { value: MOON_DIRECTION.clone() },
      uMoonSize: { value: 0.088 },
      uTime: { value: 0 },
      uStars: { value: 1 },
    },
    vertexShader: SKY_VERTEX,
    fragmentShader: SKY_FRAGMENT,
    side: THREE.BackSide,
    depthWrite: false,
    fog: false,
  });
  const sky = new THREE.Mesh(geometry, material);
  sky.frustumCulled = false;
  sky.renderOrder = -10;
  return sky;
}

function createMistTexture() {
  const canvas = document.createElement('canvas');
  canvas.width = 128;
  canvas.height = 128;
  const context = canvas.getContext('2d');
  const gradient = context.createRadialGradient(64, 64, 4, 64, 64, 64);
  gradient.addColorStop(0, 'rgba(190, 232, 222, 0.62)');
  gradient.addColorStop(0.45, 'rgba(122, 184, 181, 0.28)');
  gradient.addColorStop(1, 'rgba(48, 93, 111, 0)');
  context.fillStyle = gradient;
  context.fillRect(0, 0, 128, 128);
  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  return texture;
}

// A vertical falloff with a few bright chop lines: the moon's reflection on the pool.
function createStreakTexture() {
  const canvas = document.createElement('canvas');
  canvas.width = 32;
  canvas.height = 128;
  const context = canvas.getContext('2d');
  const gradient = context.createLinearGradient(0, 0, 0, 128);
  gradient.addColorStop(0, 'rgba(150, 214, 214, 0)');
  gradient.addColorStop(0.34, 'rgba(178, 232, 226, 0.5)');
  gradient.addColorStop(0.55, 'rgba(214, 244, 240, 0.72)');
  gradient.addColorStop(0.78, 'rgba(150, 214, 214, 0.34)');
  gradient.addColorStop(1, 'rgba(120, 186, 190, 0)');
  context.fillStyle = gradient;
  context.fillRect(0, 0, 32, 128);
  for (let i = 0; i < 14; i += 1) {
    const y = 6 + i * 9 + (i % 3) * 2;
    context.fillStyle = `rgba(226, 248, 244, ${0.05 + (i % 4) * 0.035})`;
    context.fillRect(2 + (i % 5) * 3, y, 28 - (i % 5) * 5, 1 + (i % 2));
  }
  const edge = context.createLinearGradient(0, 0, 32, 0);
  edge.addColorStop(0, 'rgba(0, 0, 0, 1)');
  edge.addColorStop(0.5, 'rgba(0, 0, 0, 0)');
  edge.addColorStop(1, 'rgba(0, 0, 0, 1)');
  context.globalCompositeOperation = 'destination-out';
  context.fillStyle = edge;
  context.fillRect(0, 0, 32, 128);
  context.globalCompositeOperation = 'source-over';
  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  return texture;
}

function addAtmosphere(root, random) {
  const mistTexture = createMistTexture();
  const mist = new THREE.Group();
  // Seven wide, low sheets rather than five narrow ones: at the old 0.055 opacity the haze was
  // invisible and the mid-ground had nothing to sit in.
  for (let i = 0; i < 7; i += 1) {
    const layer = new THREE.Mesh(
      new THREE.PlaneGeometry(30 + i * 5, 16 + i * 2),
      new THREE.MeshBasicMaterial({ map: mistTexture, color: 0x8bb6b2, transparent: true, opacity: 0.1, depthWrite: false, side: THREE.DoubleSide }),
    );
    layer.rotation.x = -Math.PI / 2;
    layer.rotation.z = random() * Math.PI;
    layer.position.set(-30 + i * 10, 0.16 + i * 0.015, -26 + (i % 2) * 44);
    layer.renderOrder = 1;
    mist.add(layer);
  }
  root.add(mist);
  const motePositions = new Float32Array(180 * 3);
  for (let i = 0; i < 180; i += 1) {
    motePositions[i * 3] = -PLAYABLE_BOUND + random() * PLAYABLE_BOUND * 2;
    motePositions[i * 3 + 1] = 0.6 + random() * 5.8;
    motePositions[i * 3 + 2] = -PLAYABLE_BOUND + random() * PLAYABLE_BOUND * 2;
  }
  const moteGeometry = new THREE.BufferGeometry();
  moteGeometry.setAttribute('position', new THREE.BufferAttribute(motePositions, 3));
  const motes = new THREE.Points(moteGeometry, new THREE.PointsMaterial({ color: 0x8fe4d2, size: 0.1, transparent: true, opacity: 0.48, depthWrite: false, sizeAttenuation: true }));
  root.add(motes);
  return { mist, mistTexture, motes };
}

function addGroundDetails(root, random, groundHeight) {
  const dummy = new THREE.Object3D();
  const tint = new THREE.Color();
  // Tufts were 0.08 m wide and 0.55 m tall over 220 tries in an 80 m square: below a pixel at play
  // distance, so the floor stayed bare. Taller, wider, denser, and a stop lighter, they now read
  // as ground cover from the follow camera and still cost one instanced draw each.
  const grass = new THREE.InstancedMesh(new THREE.ConeGeometry(0.16, 0.95, 3), material(0x5d8f7c, 0.98), 420);
  let grassCount = 0;
  for (let i = 0; i < 1500 && grassCount < 420; i += 1) {
    const x = -PLAYABLE_BOUND + random() * PLAYABLE_BOUND * 2;
    const z = -PLAYABLE_BOUND + random() * PLAYABLE_BOUND * 2;
    if (Math.abs(x) < 4 && z > -28 && z < 28) continue;
    if (Math.hypot(x + 9, z - 10) < 7) continue;
    dummy.position.set(x, groundHeight(x, z) + 0.34, z);
    dummy.rotation.set(0, random() * Math.PI, 0);
    const scale = 0.7 + random() * 1.1;
    dummy.scale.set(scale, scale * (0.8 + random() * 0.9), scale);
    dummy.updateMatrix();
    grass.setMatrixAt(grassCount, dummy.matrix);
    tint.setHSL(0.3 + random() * 0.12, 0.24 + random() * 0.2, 0.26 + random() * 0.18);
    grass.setColorAt(grassCount, tint);
    grassCount += 1;
  }
  grass.count = grassCount;
  grass.instanceMatrix.needsUpdate = true;
  if (grass.instanceColor) grass.instanceColor.needsUpdate = true;
  grass.receiveShadow = true;
  root.add(grass);

  const rocks = new THREE.InstancedMesh(new THREE.DodecahedronGeometry(0.4, 0), material(0x566273, 0.98), 96);
  let rockCount = 0;
  for (let i = 0; i < 320 && rockCount < 96; i += 1) {
    const x = -PLAYABLE_BOUND + random() * PLAYABLE_BOUND * 2;
    const z = -PLAYABLE_BOUND + random() * PLAYABLE_BOUND * 2;
    if (Math.abs(x) < 3 && z > -22 && z < 22) continue;
    dummy.position.set(x, groundHeight(x, z) + 0.14, z);
    dummy.rotation.set(random() * 0.5, random() * Math.PI, random() * 0.5);
    const scale = 0.5 + random() * 1.35;
    dummy.scale.set(scale, scale * (0.6 + random() * 0.45), scale);
    dummy.updateMatrix();
    rocks.setMatrixAt(rockCount, dummy.matrix);
    tint.setHSL(0.55 + random() * 0.1, 0.12 + random() * 0.16, 0.3 + random() * 0.2);
    rocks.setColorAt(rockCount, tint);
    rockCount += 1;
  }
  rocks.count = rockCount;
  rocks.instanceMatrix.needsUpdate = true;
  if (rocks.instanceColor) rocks.instanceColor.needsUpdate = true;
  rocks.castShadow = true;
  rocks.receiveShadow = true;
  root.add(rocks);

  // Loose shingle: flat chips that catch the key light and give the floor mid-frequency texture
  // between the tufts and the boulders.
  const shingle = new THREE.InstancedMesh(new THREE.CylinderGeometry(0.26, 0.3, 0.09, 5), material(0x6a7383, 0.98), 240);
  let shingleCount = 0;
  for (let i = 0; i < 900 && shingleCount < 240; i += 1) {
    const x = -PLAYABLE_BOUND + random() * PLAYABLE_BOUND * 2;
    const z = -PLAYABLE_BOUND + random() * PLAYABLE_BOUND * 2;
    dummy.position.set(x, groundHeight(x, z) + 0.05, z);
    dummy.rotation.set((random() - 0.5) * 0.2, random() * Math.PI, (random() - 0.5) * 0.2);
    const scale = 0.6 + random() * 1.2;
    dummy.scale.set(scale, 1, scale);
    dummy.updateMatrix();
    shingle.setMatrixAt(shingleCount, dummy.matrix);
    tint.setHSL(0.5 + random() * 0.14, 0.1 + random() * 0.16, 0.26 + random() * 0.2);
    shingle.setColorAt(shingleCount, tint);
    shingleCount += 1;
  }
  shingle.count = shingleCount;
  shingle.instanceMatrix.needsUpdate = true;
  if (shingle.instanceColor) shingle.instanceColor.needsUpdate = true;
  shingle.receiveShadow = true;
  root.add(shingle);
  return { grass, rocks, shingle };
}
function addBossStage(root) {
  const stage = new THREE.Group();
  stage.position.set(0, 0, -29);
  const stone = material(0x251d32, 0.94);
  const edge = material(0x5c3a4d, 0.76, 0.12);
  const dais = new THREE.Mesh(new THREE.CylinderGeometry(6.2, 6.6, 0.36, 8), stone);
  dais.position.y = 0.18;
  stage.add(dais);
  const inset = new THREE.Mesh(new THREE.CylinderGeometry(4.7, 4.9, 0.08, 8), new THREE.MeshBasicMaterial({ color: 0x6b243e, transparent: true, opacity: 0.22, blending: THREE.AdditiveBlending, depthWrite: false }));
  inset.position.y = 0.4;
  stage.add(inset);
  const arch = new THREE.Mesh(new THREE.TorusGeometry(4.7, 0.22, 6, 28, Math.PI), edge);
  arch.rotation.z = Math.PI;
  arch.position.y = 4.5;
  stage.add(arch);
  const disc = new THREE.Mesh(new THREE.CircleGeometry(2.15, 32), new THREE.MeshBasicMaterial({ color: 0xe26b78, transparent: true, opacity: 0.38, blending: THREE.AdditiveBlending, depthWrite: false, side: THREE.DoubleSide }));
  disc.position.set(0, 5.35, -0.16);
  stage.add(disc);
  const discRing = new THREE.Mesh(new THREE.TorusGeometry(2.45, 0.07, 6, 28), new THREE.MeshBasicMaterial({ color: 0xf2c36e, transparent: true, opacity: 0.46, blending: THREE.AdditiveBlending, depthWrite: false }));
  discRing.position.set(0, 5.35, -0.1);
  stage.add(discRing);
  for (const x of [-4.2, 4.2]) {
    const column = new THREE.Mesh(new THREE.CylinderGeometry(0.34, 0.48, 6.4, 6), stone);
    column.position.set(x, 3.2, 0.1);
    stage.add(column);
    const capital = new THREE.Mesh(new THREE.BoxGeometry(1.05, 0.28, 1.05), edge);
    capital.position.set(x, 6.38, 0.1);
    stage.add(capital);
  }
  const banners = [];
  for (const x of [-2.65, 2.65]) {
    const banner = new THREE.Mesh(new THREE.PlaneGeometry(0.82, 3.1), new THREE.MeshBasicMaterial({ color: 0x7d2948, transparent: true, opacity: 0.55, side: THREE.DoubleSide }));
    banner.position.set(x, 3.55, 0.18);
    stage.add(banner);
    banners.push(banner);
  }
  stage.userData = { disc, discRing, banners };
  stage.visible = false;
  root.add(stage);
  return stage;
}

// A ward pylon reads cold and dead until it is paid for: a stepped plinth, a tapered iron post, a
// brazier bowl, and an ember sunk in it. Igniting swaps the ember for a flame, raises a real point
// light, and spins the rune ring up — the same object in three states, so the player can tell a
// spent pylon from an untouched one across the dark without reading a single word.
function addWardPylons(root, groundHeight) {
  const plinthGeometry = new THREE.CylinderGeometry(0.92, 1.12, 0.34, 8);
  const stepGeometry = new THREE.CylinderGeometry(0.66, 0.8, 0.26, 8);
  const postGeometry = new THREE.CylinderGeometry(0.14, 0.24, 2.9, 6);
  const bowlGeometry = new THREE.CylinderGeometry(0.52, 0.28, 0.44, 8);
  const ringGeometry = new THREE.TorusGeometry(1.15, 0.045, 6, 24);
  const emberGeometry = new THREE.OctahedronGeometry(0.2, 0);
  const flameGeometry = new THREE.ConeGeometry(0.34, 0.95, 6);
  const stoneMaterial = material(0x2b3542, 0.94, 0.06);
  const ironMaterial = material(0x4d5765, 0.72, 0.22);
  const pylons = new Map();

  for (const spec of LANTERN_PYLONS) {
    const group = new THREE.Group();
    group.position.set(spec.x, groundHeight(spec.x, spec.z), spec.z);
    group.rotation.y = (spec.x + spec.z) * 0.11;
    const plinth = new THREE.Mesh(plinthGeometry, stoneMaterial);
    plinth.position.y = 0.17;
    group.add(plinth);
    const step = new THREE.Mesh(stepGeometry, stoneMaterial);
    step.position.y = 0.47;
    group.add(step);
    const post = new THREE.Mesh(postGeometry, ironMaterial);
    post.position.y = 2.02;
    group.add(post);
    const bowl = new THREE.Mesh(bowlGeometry, ironMaterial);
    bowl.position.y = 3.5;
    group.add(bowl);
    const ring = new THREE.Mesh(ringGeometry, glowMaterial(0x6de4e0, 1.1));
    ring.rotation.x = Math.PI / 2;
    ring.position.y = 0.72;
    ring.material.opacity = 0.12;
    group.add(ring);
    const ember = new THREE.Mesh(emberGeometry, glowMaterial(0x5c6c86, 0.7));
    ember.position.y = 3.78;
    group.add(ember);
    const flame = new THREE.Mesh(flameGeometry, glowMaterial(0xe8bb69, 2.2));
    flame.position.y = 4.02;
    flame.visible = false;
    group.add(flame);
    const emitter = makeEmitter(0xe8bb69, group, 3.9, 15, 0);
    group.userData = { type: 'pylon', id: spec.id, label: `${spec.name} pylon`, note: spec.note, district: spec.district, ember, flame, emitter, ring, lit: false, charge: 0, phase: (spec.x * 0.7 + spec.z * 1.3) % 6.283 };
    root.add(group);
    pylons.set(spec.id, group);
  }
  return pylons;
}


export function createWorld(scene, seed = 24717) {
  const random = makeRng(seed);
  const groundHeight = (x, z) => Math.sin(x * 0.21 + seed) * 0.1 + Math.cos(z * 0.17 - seed) * 0.08 + Math.sin((x + z) * 0.08) * 0.06;
  // Moonlight reach on the ground, matching the sky's moon bearing. Shared with the vertex colour
  // bake so the lit pool and the disc in the sky agree.
  const moonFall = (x, z) => 1 - 0.42 * Math.min(1, Math.hypot(x - MOON_BEARING_X, z - MOON_BEARING_Z) / MOON_REACH);
  const edgeFall = (x, z) => {
    const t = Math.min(1, Math.max(0, (Math.hypot(x, z) - 24) / 38));
    return 1 - 0.34 * t * t;
  };
  const root = new THREE.Group();
  root.name = 'VesperaWild';
  scene.add(root);
  const sky = createSkyDome();
  root.add(sky);
  const atmosphere = addAtmosphere(root, random);
  const world = { root, seed, activeDistrict: 'moonwell', interactables: [], sigils: new Map(), blockers: [], lanterns: [], trees: [], landmarks: [], covers: [], water: null, moon: null, sky, atmosphere, details: null, bossStage: null, gate: null, time: 0 };
  const groundGeometry = new THREE.PlaneGeometry(TERRAIN_SIZE, TERRAIN_SIZE, 100, 100);
  groundGeometry.rotateX(-Math.PI / 2);
  const groundPositions = groundGeometry.attributes.position;
  for (let i = 0; i < groundPositions.count; i += 1) {
    const x = groundPositions.getX(i);
    const z = groundPositions.getZ(i);
    groundPositions.setY(i, groundHeight(x, z));
  }
  // The floor used to carry a +/-8% tint that vanished under a near-black albedo, so the arena read
  // as an empty void with figures floating in it. Three readable layers go into the bake instead:
  // broad patches, a fine mottle, and the directional falloff that turns the moon into a light pool.
  const groundColors = new Float32Array(groundPositions.count * 3);
  for (let i = 0; i < groundPositions.count; i += 1) {
    const x = groundPositions.getX(i);
    const z = groundPositions.getZ(i);
    const patch = 0.5 + 0.5 * Math.sin(x * 0.11 + 1.3) * Math.cos(z * 0.09 - 0.7);
    const mottle = 0.5 + 0.5 * Math.sin(x * 0.63 - z * 0.41 + seed);
    const value = (0.6 + patch * 0.36 + mottle * 0.16 + random() * 0.07) * moonFall(x, z) * edgeFall(x, z);
    groundColors[i * 3] = value * (0.88 + mottle * 0.16);
    groundColors[i * 3 + 1] = value * (0.97 + mottle * 0.06);
    groundColors[i * 3 + 2] = value * (1.12 - mottle * 0.12);
    const trail = trailWeight(x, z) * 0.65;
    groundColors[i * 3] = groundColors[i * 3] * (1 - trail) + 1.05 * trail;
    groundColors[i * 3 + 1] = groundColors[i * 3 + 1] * (1 - trail) + 0.92 * trail;
    groundColors[i * 3 + 2] = groundColors[i * 3 + 2] * (1 - trail) + 0.72 * trail;
  }
  groundGeometry.setAttribute('color', new THREE.Float32BufferAttribute(groundColors, 3));
  groundGeometry.computeVertexNormals();
  const groundMaterial = material(DISTRICTS.moonwell.ground, 1);
  groundMaterial.vertexColors = true;
  const ground = new THREE.Mesh(groundGeometry, groundMaterial);
  ground.receiveShadow = true;
  root.add(ground);
  world.ground = ground;
  // The pool was a 0.22-roughness metal disc, so the key light burned a flat cream ellipse across
  // the middle of the play space. A rougher dielectric plus an explicit moon streak reads as water
  // instead of as a blown highlight.
  const water = new THREE.Mesh(new THREE.CircleGeometry(6, 40), new THREE.MeshStandardMaterial({ color: 0x08202b, roughness: 0.46, metalness: 0.06, transparent: true, opacity: 0.9 }));
  water.rotation.x = -Math.PI / 2;
  water.position.set(-9, 0.09, 10);
  root.add(water);
  world.water = water;
  world.waterFX = enhanceSurface(water.material, { water: true });
  world.effects = createThreeEffects(THREE, root, { seed });
  const streak = new THREE.Mesh(new THREE.PlaneGeometry(2.4, 11), new THREE.MeshBasicMaterial({
    map: createStreakTexture(), color: 0xa9e2dd, transparent: true, opacity: 0.34,
    blending: THREE.AdditiveBlending, depthWrite: false,
  }));
  streak.rotation.x = -Math.PI / 2;
  streak.rotation.z = Math.atan2(MOON_BEARING_X + 9, MOON_BEARING_Z - 10);
  streak.position.set(-9, 0.13, 10);
  root.add(streak);
  world.moonStreak = streak;
  for (const [ax, az, bx, bz] of FRONTIER_TRAILS) addPath(root, [[ax, az], [bx, bz]], 0x59615c, 1.6);
  addPath(root, [[0, 31], [0, 17], [-4, 7], [-9, -5], [-18, -12]], 0x435b63, 2.8);
  addPath(root, [[0, 7], [8, 5], [17, -8], [19, -20]], 0x59484d, 2.3);
  addPath(root, [[1, 6], [9, 11], [18, 15], [24, 24]], 0x3e535c, 2.2);
  const canopy = addTrees(root, random, 152);
  world.cutaway = createCutaway(THREE);
  for (const mesh of root.children.filter((child) => child.name.startsWith('tree-'))) world.cutaway.register(mesh);
  world.trees = canopy.trees;
  for (const tree of canopy.trees) world.blockers.push({ x: tree.x, z: tree.z, radius: 0.75 * tree.scale });
  world.details = addGroundDetails(root, random, groundHeight);
  for (const spec of SIGILS) {
    const sigil = addSigil(root, spec);
    world.sigils.set(spec.id, sigil);
    world.interactables.push(sigil);
  }
  const gate = addGate(root);
  world.gate = gate;
  world.interactables.push(gate);
  world.lanterns.push(addLantern(root, -5, 13, 0xe8bb69), addLantern(root, 8, 4, 0x6de4e0), addLantern(root, 17, -8, 0xef6b72), addLantern(root, 15, 15, 0xa1a0e8), addLantern(root, -19, -7, 0xe8bb69));
  const ruinMaterial = material(0x283245, 0.98);
  for (const [x, z, scale] of [[-27, 8, 1], [26, -22, 1.2], [27, 21, .8], [-25, -24, 1.1]]) {
    const ruin = new THREE.Group();
    ruin.position.set(x, 0, z);
    ruin.scale.setScalar(scale);
    const pillar = new THREE.Mesh(new THREE.BoxGeometry(1.1, 4.5, 1.1), ruinMaterial);
    pillar.position.y = 2.2;
    ruin.add(pillar);
    const cap = new THREE.Mesh(new THREE.BoxGeometry(1.8, 0.35, 1.8), ruinMaterial);
    cap.position.y = 4.5;
    ruin.add(cap);
    root.add(ruin);
    world.cutaway.register(ruin);
    world.blockers.push({ x, z, radius: 1.4 * scale });
  }
  const districtLayer = addDistrictLayer(root, random);
  world.landmarks = districtLayer.landmarks;
  world.covers = districtLayer.covers;
  for (const landmark of world.landmarks) world.blockers.push({ x: landmark.position.x, z: landmark.position.z, radius: 1.35 * landmark.scale.x });
  world.pylons = addWardPylons(root, groundHeight);
  for (const pylon of world.pylons.values()) world.interactables.push(pylon);
  for (const pylon of world.pylons.values()) world.blockers.push({ x: pylon.position.x, z: pylon.position.z, radius: 0.85 });
  for (const cover of world.covers) world.blockers.push({ x: cover.position.x, z: cover.position.z, radius: 1.28 * cover.scale.x });
  for (const landmark of world.landmarks) world.interactables.push(landmark);
  world.bossStage = addBossStage(root);
  for (const group of [...world.landmarks, ...world.covers, ...world.pylons.values(), world.bossStage]) world.cutaway.register(group);
  // 0.0145 left the far bank crisp while the floor underneath stayed black, so distance read as
  // nothing at all. Denser exponential fog tinted to the district sky now separates near from far.
  scene.fog = new THREE.FogExp2(DISTRICTS.moonwell.fog, 0.023);
  scene.background = new THREE.Color(0x090c1b);
  const hemi = new THREE.HemisphereLight(0x98c9d6, 0x3a3350, 1.2);
  scene.add(hemi);
  // The key light now comes from the moon's own bearing, so shadows fall away from the disc, the
  // baked ground pool sits under the light that casts it, and the whole scene agrees on a source.
  const key = new THREE.DirectionalLight(0xb7d7e3, 2.5);
  key.position.copy(MOON_DIRECTION).multiplyScalar(40);
  key.castShadow = true;
  key.shadow.mapSize.set(1024, 1024);
  key.shadow.camera.left = -22;
  key.shadow.camera.right = 22;
  key.shadow.camera.top = 22;
  key.shadow.camera.bottom = -22;
  key.shadow.camera.far = 100;
  key.shadow.bias = -0.00035;
  key.shadow.normalBias = 0.035;
  scene.add(key);
  scene.add(key.target);
  const moonOffset = key.position.clone();
  // A dim counter-fill from the opposite side keeps the shaded half of every silhouette readable
  // instead of crushing it to the fog colour.
  const bounce = new THREE.DirectionalLight(0x9fb4d8, 0.55);
  bounce.position.set(-key.position.x * 0.6, 14, -key.position.z * 0.6);
  scene.add(bounce);
  const moonLight = new THREE.PointLight(0x6de4e0, 2.2, 38, 2);
  moonLight.position.set(-16, 8, -20);
  scene.add(moonLight);
  world.hemi = hemi;
  world.key = key;
  world.bounce = bounce;
  world.moonLight = moonLight;
  // A fixed pool of pool-lights, resized never: the count is part of the shader program key, so a
  // constant count means one compile instead of one per distance band the player walks through.
  const slotCount = 4;
  const slots = [];
  const slotLights = [];
  for (let index = 0; index < slotCount; index += 1) {
    const light = new THREE.PointLight(0xffffff, 0, 8, 2);
    light.position.set(0, -100, 0);
    scene.add(light);
    slots.push(null);
    slotLights.push(light);
  }
  world.emitters = [
    ...world.lanterns.map((lantern) => lantern.userData.emitter),
    ...world.sigils.values().map((sigil) => sigil.userData.emitter),
    ...world.pylons.values().map((pylon) => pylon.userData.emitter),
  ];
  world.slots = slots;
  // Selection is a fixed four-pass min scan over thirteen emitters: no sort, no closure, no
  // garbage, and it runs every frame so a lit pylon takes its slot the frame it catches fire.
  // `claimed` is what makes a pass unable to hand the same lantern to two slots, and the score
  // bonus is what makes a pool keep its light while the player walks past it.
  const claimed = [];
  const fillSlots = (focusX, focusZ) => {
    claimed.length = 0;
    for (let slotIndex = 0; slotIndex < slotCount; slotIndex += 1) {
      let best = null;
      let bestScore = Infinity;
      for (const emitter of world.emitters) {
        if (!emitter.live || emitter.intensity <= 0 || claimed.includes(emitter)) continue;
        const dx = emitter.x - focusX;
        const dz = emitter.z - focusZ;
        const score = (slots.includes(emitter) ? 0.02 : 1) * (dx * dx + dz * dz);
        if (score < bestScore) { bestScore = score; best = emitter; }
      }
      if (best) claimed.push(best);
      slots[slotIndex] = best;
    }
    for (let slotIndex = 0; slotIndex < slotCount; slotIndex += 1) {
      const emitter = slots[slotIndex];
      const light = slotLights[slotIndex];
      if (!emitter) { light.intensity = 0; continue; }
      light.position.set(emitter.x, emitter.y, emitter.z);
      light.color.setHex(emitter.color);
      light.distance = emitter.range;
      light.intensity = emitter.intensity;
    }
  };
  world.lightSlotBudget = slotCount;
  world.slotLights = slotLights;
  // What the pool is actually doing, for the resilience suite: a light that should have reached the
  // floor and did not is a silent hole in the lighting, and a draw-call count cannot see it.
  world.lightSlotReport = () => {
    let unslottedInRange = 0;
    for (const emitter of world.emitters) {
      if (!emitter.live || emitter.intensity <= 0 || slots.includes(emitter)) continue;
      const dx = emitter.x - lightFocus.x;
      const dz = emitter.z - lightFocus.z;
      if (Math.hypot(dx, dz) <= emitter.range) unslottedInRange += 1;
    }
    return { budget: slotCount, lit: slots.filter(Boolean).length, unslottedInRange, focus: { x: lightFocus.x, z: lightFocus.z } };
  };
  world.getGroundHeight = groundHeight;
  world.isBlocked = (x, z, radius = 0.55) => world.blockers.some(blocker => Math.hypot(x - blocker.x, z - blocker.z) < blocker.radius + radius);
  // One function owns every pylon's look, so the channel, the ignition and the save load can each
  // hand it a ward state instead of poking at meshes from three different places.
  world.syncWardPylons = (wardState = { lit: [], charge: {}, snuffed: [] }) => {
    const lit = new Set((Array.isArray(wardState?.lit) ? wardState.lit : []).map(String));
    const doused = new Set((Array.isArray(wardState?.snuffed) ? wardState.snuffed : []).map(String));
    for (const [id, pylon] of world.pylons) {
      const data = pylon.userData;
      const isLit = lit.has(id);
      // A pylon a wick-servant put out is a fourth state, not a never-lit one: the bowl is ringed
      // and cold-dark instead of bare, so a doused hearth reads as something that was taken back.
      const isSnuffed = !isLit && doused.has(id);
      const charge = Math.max(0, Math.min(1, Number(wardState?.charge?.[id]) || 0));
      data.lit = isLit;
      data.snuffed = isSnuffed;
      data.charge = charge;
      data.flame.visible = isLit;
      data.ember.visible = !isLit;
      data.ember.material.opacity = isSnuffed ? 0.3 : 0.45 + charge * 0.55;
      data.ember.scale.setScalar(isSnuffed ? 0.62 : 0.8 + charge * 0.9);
      data.emitter.intensity = isLit ? 2.6 : isSnuffed ? 0.16 : charge * 1.4;
      data.ring.material.opacity = isLit ? 0.62 : isSnuffed ? 0.34 : 0.12 + charge * 0.5;
      data.ring.material.color.setHex(isLit ? 0xe8bb69 : isSnuffed ? 0xc25a6a : 0x6de4e0);
      data.ember.material.color.setHex(isLit ? 0xe8bb69 : isSnuffed ? 0x7a6ea0 : charge > 0.02 ? 0xffc46a : 0x5c6c86);
    }
  };
  world.syncWardPylons({ lit: [], charge: {} });
  world.getNearestInteractable = (position, maxDistance = 2.7) => {
    let nearest = null;
    let nearestDistance = maxDistance;
    for (const item of world.interactables) {
      if (item.userData.collected || item.userData.discovered || (item.userData.type === 'landmark' && !item.userData.active) || (item.userData.type === 'pylon' && item.userData.lit)) continue;
      const distance = position.distanceTo(item.position);
      if (distance < nearestDistance) { nearest = item; nearestDistance = distance; }
    }
    return nearest;
  };
  world.setDistrict = (id) => {
    const district = DISTRICTS[id] || DISTRICTS.moonwell;
    world.activeDistrict = id in DISTRICTS ? id : 'moonwell';
    world.assetLoader?.setDistrict(world.activeDistrict);
    world.ground.material.color.setHex(district.ground);
    scene.fog.color.setHex(district.fog);
    scene.fog.density = district.fogDensity;
    scene.background.setHex(district.fog);
    const sky = world.sky.material.uniforms;
    sky.uZenith.value.setHex(district.zenith);
    sky.uMid.value.setHex(district.mid);
    sky.uHorizon.value.setHex(district.fog);
    sky.uGlow.value.setHex(district.accent);
    sky.uMoon.value.setHex(district.moon);
    sky.uStars.value = district.stars;
    // Scaled down: the underside bounce is a fill, not a second key, or every up-facing surface
    // washes out and the moonlit read collapses into flat daylight.
    hemi.groundColor.setHex(district.ground).multiplyScalar(0.38).lerp(mixScratch.setHex(district.accent), 0.12);
    bounce.color.setHex(district.mid);
    world.atmosphere.motes.material.color.setHex(district.accent);
    for (const layer of world.atmosphere.mist.children) layer.material.color.setHex(district.fog);
    world.moonStreak.material.color.setHex(district.moon);
    world.bossStage.visible = world.activeDistrict === 'red-chapel' || world.activeDistrict === 'chapel';
    world.bossStage.userData.disc.material.color.setHex(district.accent);
    world.bossStage.userData.discRing.material.color.setHex(0xf2c36e);
    for (const banner of world.bossStage.userData.banners) banner.material.color.setHex(district.accent);
    hemi.color.setHex(district.color);
    key.color.setHex(district.color);
    moonLight.color.setHex(district.accent);
    moonLight.intensity = district === 'red-chapel' || district === 'chapel' ? 3.0 : 2.2;
    world.gate.userData.marker.material.color.setHex(district.accent);
    for (const landmark of world.landmarks) landmark.userData.active = landmark.userData.districts.includes(world.activeDistrict);
    for (const cover of world.covers) cover.userData.cue?.material?.color.setHex(district.accent);
    world.gate.userData.base.material.color.setHex(district.accent);
  };
  // The light slots follow whoever the camera is watching, so the title orbit, a cutscene and a
  // run all light the same pool without the run having to exist yet.
  const lightFocus = new THREE.Vector3();
  world.update = (dt, elapsed, focus = null) => {
    world.time += dt;
    const pulse = 0.5 + 0.5 * Math.sin(elapsed * 1.7);
    for (const lantern of world.lanterns) {
      const cage = lantern.userData.cage;
      const emitter = lantern.userData.emitter;
      if (cage) cage.scale.setScalar(0.92 + pulse * 0.16);
      if (emitter) emitter.intensity = 1.1 + pulse * 0.55;
    }
    for (const sigil of world.sigils.values()) {
      if (sigil.userData.collected) { sigil.userData.emitter.live = false; continue; }
      if (sigil.userData.crystal) {
        sigil.userData.crystal.rotation.y += dt * 1.5;
        sigil.userData.crystal.position.y = Math.sin(elapsed * 2 + sigil.position.x) * 0.08;
      }
      if (sigil.userData.ring) sigil.userData.ring.rotation.z += dt * 0.8;
      sigil.userData.emitter.intensity = 1.15 + pulse * 0.9;
      sigil.userData.emitter.live = true;
    }
    canopy.apply(elapsed);
    world.atmosphere.motes.rotation.y = elapsed * 0.012;
    world.atmosphere.motes.position.y = Math.sin(elapsed * 0.22) * 0.08;
    for (let i = 0; i < world.atmosphere.mist.children.length; i += 1) {
      world.atmosphere.mist.children[i].material.opacity = 0.075 + (0.5 + 0.5 * Math.sin(elapsed * 0.18 + i * 1.7)) * 0.05;
    }
    if (world.gate?.userData.marker) world.gate.userData.marker.rotation.y += dt * 1.4;
    if (world.gate?.userData.base) world.gate.userData.base.rotation.y -= dt * 0.5;
    for (const cover of world.covers) {
      const cue = cover.userData.cue;
      if (!cue) continue;
      const localPulse = 0.5 + 0.5 * Math.sin(elapsed * 2.2 + cover.userData.phase);
      cue.rotation.y += dt * 1.35;
      cue.position.y = 1.4 + localPulse * 0.12;
      if (cue.material) cue.material.opacity = 0.38 + localPulse * 0.52;
    }
    for (const landmark of world.landmarks) {
      const beacon = landmark.userData.beacon;
      if (!beacon) continue;
      const localPulse = 0.5 + 0.5 * Math.sin(elapsed * 1.8 + landmark.userData.phase);
      beacon.rotation.y += dt * (landmark.userData.active ? 1.2 : 0.35);
      beacon.scale.setScalar(landmark.userData.discovered ? 0.62 : 0.84 + localPulse * (landmark.userData.active ? 0.36 : 0.14));
      if (beacon.material) beacon.material.opacity = landmark.userData.discovered ? 0.18 : landmark.userData.active ? 0.5 + localPulse * 0.5 : 0.24 + localPulse * 0.2;
    }
    if (world.water?.material) {
      world.waterFX.update(world.effects.root.userData.reducedMotion ? 0 : elapsed);
      world.effects.setWeather(world.activeDistrict === 'greyfen');
      world.effects.update(elapsed);
      world.water.material.opacity = 0.82 + pulse * 0.1;
      world.water.position.y = 0.09 + Math.sin(elapsed * 0.7) * 0.012;
      world.moonStreak.material.opacity = 0.24 + pulse * 0.2;
      world.moonStreak.position.y = world.water.position.y + 0.04;
    }
    for (const pylon of world.pylons.values()) {
      const data = pylon.userData;
      const localPulse = 0.5 + 0.5 * Math.sin(elapsed * (data.lit ? 3.1 : 1.4) + data.phase);
      if (data.ring) data.ring.rotation.z += dt * (data.lit ? 0.9 : 0.25 + data.charge * 3.4);
      if (data.flame?.visible) {
        data.flame.scale.set(0.85 + localPulse * 0.28, 0.9 + localPulse * 0.35, 0.85 + localPulse * 0.28);
        data.flame.rotation.y += dt * 1.8;
        data.emitter.intensity = 2.2 + localPulse * 1.1;
      }
      if (data.ember?.visible) {
        data.ember.position.y = 3.78 + localPulse * (0.04 + data.charge * 0.16);
        data.ember.rotation.y += dt * 0.7;
      }
    }
    if (focus) lightFocus.copy(focus);
    key.target.position.set(snappedShadowFocus(lightFocus.x), 0, snappedShadowFocus(lightFocus.z));
    key.position.copy(key.target.position).add(moonOffset);
    key.target.updateMatrixWorld();
    fillSlots(lightFocus.x, lightFocus.z);
    world.sky.material.uniforms.uTime.value = elapsed;
  };
  world.dispose = () => {
    world.assetLoader?.dispose();
    world.cutaway?.dispose();
    world.effects.dispose();
    world.waterFX.dispose();
    const geometries = new Set();
    const materials = new Set();
    root.traverse(object => {
      if (object.geometry) geometries.add(object.geometry);
      if (object.material) {
        const list = Array.isArray(object.material) ? object.material : [object.material];
        for (const item of list) materials.add(item);
      }
    });
    for (const geometry of geometries) geometry.dispose();
    for (const item of materials) item.dispose();
    scene.remove(root);
    world.atmosphere.mistTexture.dispose();
    world.moonStreak.material.map?.dispose();
    scene.remove(hemi, key, key.target, bounce, moonLight, ...slotLights);
  };
  world.setDistrict('moonwell');
  if (typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('worldAssets') === '1') {
    world.assetLoader = createWorldAssetLoader(world);
    world.assetLoader.setDistrict(world.activeDistrict);
  }
  return world;
}
